import { AnimatePresence } from 'framer-motion';
import { FC, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { SwitchIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { CrossfadePanel, CrossfadeText } from '../../../components/motion/index';
import { useSelected } from '../../../hooks/company/index';
import { IssuedSharesFormula } from '../../../types/company.types';
import { Snapshot } from '../../../types/snapshot.types';
import { GraphVariant } from '../variables';
import { CapTableGraph, GraphData } from './cap-table-graph';
import { EmptyCapTableGraph } from './empty-cap-table-graph';

export type CapTableGraphWrapperProps = {
  isDesktop: boolean;
  isLoadingFinished: boolean;
  isTablet: boolean;
  selectedGraphVariant: GraphVariant;
  snapshot?: Snapshot;
  snapshotPanelKey?: string;
  toggleGraphVariant: () => void;
  wrapperClassName?: string;
};

export const CapTableGraphWrapper: FC<CapTableGraphWrapperProps> = ({
  wrapperClassName,
  selectedGraphVariant,
  isTablet,
  isDesktop,
  snapshot,
  toggleGraphVariant,
  isLoadingFinished,
  snapshotPanelKey = 'latest',
}) => {
  const { selectedCompany } = useSelected();

  const data = useMemo(() => {
    if (!snapshot?.stats?.stakeholdersTypes) {
      return [];
    }

    const totalVotingPercentage = snapshot.stats.stakeholdersTypes.reduce(
      (total, stakeholderType) => total + stakeholderType.votingPercentage,
      0,
    );

    const totalIssued = snapshot.issuedShares;

    const stakeholdersData: GraphData[] = snapshot.stats.stakeholdersTypes.map(
      ({ type, votingPercentage, issued }) => ({
        type,
        issuedPercentage: (issued * 100) / (totalIssued || 1),
        votingPercentage: (votingPercentage * 100) / (totalVotingPercentage || 1),
      }),
    );

    if (
      selectedCompany?.issuedSharesFormula === IssuedSharesFormula.ISSUED_SHARES_AND_AVAILABLE_POOL
    ) {
      const totalIssuedPercentage = stakeholdersData.reduce(
        (total, { issuedPercentage }) => total + issuedPercentage,
        0,
      );

      if (snapshot.poolDilutedShares === 0) return stakeholdersData;

      stakeholdersData.push({
        type: 'Pool',
        votingPercentage: 0,
        issuedPercentage: 100 - totalIssuedPercentage,
      });
    }

    return stakeholdersData;
  }, [
    snapshot?.stats?.stakeholdersTypes,
    snapshot?.issuedShares,
    snapshot?.poolDilutedShares,
    selectedCompany?.issuedSharesFormula,
  ]);

  const showEmptyGraph =
    (selectedGraphVariant === GraphVariant.OWNERSHIP &&
      data.every((stakeholder) => stakeholder.issuedPercentage === 0)) ||
    (selectedGraphVariant === GraphVariant.VOTING_RIGHTS &&
      data.every((stakeholder) => stakeholder.votingPercentage === 0));

  return (
    <div
      className={twMerge(
        'flex h-full min-h-full flex-col items-stretch rounded-lg bg-gray-50 p-1',
        wrapperClassName,
      )}
    >
      <div className="flex w-full shrink-0 items-center justify-between gap-1 px-4">
        <CrossfadeText
          className="block w-full py-3 text-left text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700"
          textKey={
            selectedGraphVariant === GraphVariant.OWNERSHIP
              ? 'ownership-percentage'
              : 'voting-rights-percentage'
          }
        >
          {selectedGraphVariant === GraphVariant.OWNERSHIP ? (
            <AppFormattedMessage id="OWNERSHIP_PERCENTAGE" />
          ) : (
            <AppFormattedMessage id="VOTING_RIGHTS_PERCENTAGE" />
          )}
        </CrossfadeText>
        <Button
          className="flex w-fit items-center gap-1 whitespace-nowrap"
          onClick={toggleGraphVariant}
          styleType="NONE"
        >
          <SwitchIcon />
          <CrossfadeText
            className="block text-xs font-[500] text-gray-700"
            textKey={
              isTablet || isDesktop
                ? selectedGraphVariant === GraphVariant.OWNERSHIP
                  ? 'switch-to-voting-rights'
                  : 'switch-to-ownership'
                : 'switch'
            }
          >
            {isTablet || isDesktop ? (
              selectedGraphVariant === GraphVariant.OWNERSHIP ? (
                <AppFormattedMessage id="SWITCH_TO_VOTING_RIGHTS" />
              ) : (
                <AppFormattedMessage id="SWITCH_TO_OWNERSHIP" />
              )
            ) : (
              <AppFormattedMessage id="SWITCH" />
            )}
          </CrossfadeText>
        </Button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        <AnimatePresence initial={false} mode="wait">
          {showEmptyGraph ? (
            <CrossfadePanel
              className="flex h-full min-h-0 flex-1 flex-col"
              panelKey={`${snapshotPanelKey}-empty`}
            >
              <EmptyCapTableGraph
                isDesktop={isDesktop}
                isLoadingFinished={isLoadingFinished}
                isTablet={isTablet}
              />
            </CrossfadePanel>
          ) : (
            <CrossfadePanel
              className="flex h-full min-h-0 flex-1 flex-col"
              panelKey={`${snapshotPanelKey}-chart`}
            >
              <CapTableGraph
                data={data}
                isDesktop={isDesktop}
                isTablet={isTablet}
                variant={selectedGraphVariant}
              />
            </CrossfadePanel>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
